/**
 * ThinkingAnimation Component
 *
 * Single animation zone displayed above the prompt input when Solomon is thinking.
 * Claude Code-inspired UX: one clean status line with message and elapsed time.
 *
 * Format: ✧ seeking wisdom... (32s)
 */

import { useEffect, useRef, useState } from 'react';

import { cn } from '@pray/shared/utils/styles';

import styles from './ThinkingAnimation.module.scss';

export interface ThinkingAnimationProps {
  /** Whether the animation is active (Solomon is thinking) */
  isActive: boolean;
  /** The cycling thinking message to display */
  message?: string;
  /** Additional class name */
  className?: string;
}

/**
 * Format elapsed seconds into a readable string
 * 0-59s: "32s"
 * 60s+: "1m 32s"
 */
function formatElapsedTime(seconds: number): string {
  if (seconds < 60) {
    return `${seconds}s`;
  }
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}m ${remainingSeconds}s`;
}

export function ThinkingAnimation({ isActive, message, className }: ThinkingAnimationProps) {
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const startTimeRef = useRef<number | null>(null);

  // Track elapsed time when active
  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (isActive) {
      // Reset and start timer when becoming active
      setElapsedSeconds(0);
      startTimeRef.current = Date.now();

      const interval = setInterval(() => {
        if (startTimeRef.current) {
          const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
          setElapsedSeconds(elapsed);
        }
      }, 1000);

      return () => {
        clearInterval(interval);
        startTimeRef.current = null;
      };
    }
    // Reset when becoming inactive
    setElapsedSeconds(0);
    startTimeRef.current = null;
  }, [isActive]);

  if (!isActive) return null;

  return (
    <div className={cn(styles.container, className)} data-testid="solomon-animation-zone">
      <div className={styles.statusLine}>
        <span className={styles.sparkle}>✧</span>
        <span className={styles.message}>{message || 'thinking...'}</span>
        <span className={styles.elapsedTime}>({formatElapsedTime(elapsedSeconds)})</span>
      </div>
    </div>
  );
}

export default ThinkingAnimation;
